import React, { useState } from 'react';
import { loginUser } from '../utils/api';
import './Login.css';

interface LoginProps {
  onLoginSuccess: (token: string, user: { id: number; username: string; role: 'super_admin' | 'admin' | 'customer' }) => void;
}

const generateCaptchaText = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaText, setCaptchaText] = useState(() => generateCaptchaText());
  const [userCaptcha, setUserCaptcha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (userCaptcha.trim().toLowerCase() !== captchaText) {
      setError('Invalid captcha code');
      setCaptchaText(generateCaptchaText());
      setUserCaptcha('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await loginUser(username.trim(), password.trim());
      onLoginSuccess(res.token, res.user);
    } catch (err: any) {
      setError(err.message || 'Invalid username or password');
      // Regenerate captcha on failed auth
      setCaptchaText(generateCaptchaText());
      setUserCaptcha('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="glass-card">
        <div className="login-header">
          <div className="logo-badge">🎙️</div>
          <h2>Voice AI Agent</h2>
          <p>Sign in to access your dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="captcha">Security Verification</label>
            <div className="captcha-container">
              <div className="captcha-box">{captchaText}</div>
              <button
                type="button"
                onClick={() => {
                  setCaptchaText(generateCaptchaText());
                  setUserCaptcha('');
                }}
                className="captcha-refresh-btn"
                title="Refresh Code"
                disabled={loading}
              >
                🔄
              </button>
            </div>
            <input
              id="captcha"
              type="text"
              placeholder="Type the 5 letters above"
              value={userCaptcha}
              onChange={(e) => setUserCaptcha(e.target.value)}
              disabled={loading}
              required
              autoComplete="off"
            />
          </div>
          {error && <div className="error-message">⚠️ {error}</div>}
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
